import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button, Chip, Pagination, Spinner, Table } from "@heroui/react";
import { Icon } from "@iconify/react";

import { dt } from "@/shared/helper/datetime";
import { useOrders } from "@/features/order/use-orders.ts";
import { orderTools } from "@/shared/helper/order.ts";
import { orderStatusColor } from "@/shared/helper/order-status.ts";
import {
  PAGINATION_ELLIPSIS,
  paginationRange,
} from "@/shared/helper/pagination.ts";
import { AdminOrderEntity } from "@/features/order/types.ts";
import {
  OrdersFilters,
  OrdersFiltersValue,
} from "@/features/order/ui/orders-filters.tsx";

type OrdersSearch = {
  page?: number;
} & OrdersFiltersValue;

export const Route = createFileRoute("/dashboard/orders")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): OrdersSearch => ({
    page: search.page ? Number(search.page) : undefined,
    orderId: search.orderId ? Number(search.orderId) : undefined,
    fromCity: search.fromCity ? Number(search.fromCity) : undefined,
    toCity: search.toCity ? Number(search.toCity) : undefined,
    status: (search.status as string) || undefined,
    createdFrom: (search.createdFrom as string) || undefined,
    createdTo: (search.createdTo as string) || undefined,
    fromPhone: (search.fromPhone as string) || undefined,
    toPhone: (search.toPhone as string) || undefined,
  }),
});

const LIMIT = 20;

function RouteComponent() {
  const { page, ...filters } = Route.useSearch();
  const routeNavigate = Route.useNavigate();
  const navigate = useNavigate();

  const currentPage = page ?? 1;

  const { data, isLoading, isFetching } = useOrders({
    page: currentPage,
    limit: LIMIT,
    filters: {
      orderId: filters.orderId,
      fromCityId: filters.fromCity,
      toCityId: filters.toCity,
      status: filters.status,
      createdFrom: filters.createdFrom,
      createdTo: filters.createdTo,
      fromPhone: filters.fromPhone,
      toPhone: filters.toPhone,
    },
  });

  const orders = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / LIMIT);

  const setPage = (next: number) =>
    routeNavigate({
      search: { ...filters, page: next > 1 ? next : undefined },
    });

  // Любая смена фильтра возвращает на первую страницу: на пятой странице
  // отфильтрованной выборки чаще всего уже пусто.
  const onFiltersChange = (next: OrdersFiltersValue) =>
    routeNavigate({ search: { ...next, page: undefined } });

  return (
    <div className="flex flex-col pt-4">
      <div className="flex flex-row gap-2 justify-between h-12 items-center">
        <div className="flex flex-row items-center gap-2">
          <p className="font-bold text-xl">Заказы</p>

          {isFetching && !isLoading && <Spinner size="sm" />}
        </div>
        <p className="text-sm text-neutral-500">Всего: {total}</p>
      </div>

      <OrdersFilters value={filters} onChange={onFiltersChange} />

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : orders.length === 0 ? (
        <div className="flex justify-center py-8 text-neutral-500">
          Заказы не найдены
        </div>
      ) : (
        <Table.Root className="mt-4">
          <Table.Content aria-label="Список заказов">
            <Table.Header>
              <Table.Column>ID</Table.Column>
              <Table.Column>Дата создания</Table.Column>
              <Table.Column>Откуда</Table.Column>
              <Table.Column>Куда</Table.Column>
              <Table.Column>Тариф</Table.Column>
              <Table.Column>Цена</Table.Column>
              <Table.Column>Статус</Table.Column>
              <Table.Column> </Table.Column>
            </Table.Header>
            <Table.Body items={orders}>
              {(order: AdminOrderEntity) => (
                <Table.Row id={String(order.id)}>
                  <Table.Cell className="font-mono text-sm">
                    {orderTools.formatOrderId(order.id)}
                  </Table.Cell>
                  <Table.Cell className="text-sm text-neutral-600">
                    {dt(order.createdAt).format("DD.MM.YYYY HH:mm")}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">
                        {order.fromCity.name}
                      </span>
                      <span className="text-xs text-neutral-500">
                        {order.fromAddress}
                      </span>
                      <span className="text-xs text-neutral-400">
                        {order.fromName} · {order.fromPhone}
                      </span>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">
                        {order.toCity.name}
                      </span>
                      <span className="text-xs text-neutral-500">
                        {order.toAddress}
                      </span>
                      <span className="text-xs text-neutral-400">
                        {order.toName} · {order.toPhone}
                      </span>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="text-sm">
                    {order.packages.length > 0 ? order.packages[0].name : "—"}
                  </Table.Cell>
                  <Table.Cell className="text-sm font-medium">
                    {order.packages.length > 0
                      ? `${order.packages
                          .reduce(
                            (sum: number, p: AdminOrderEntity["packages"][0]) =>
                              sum + p.sellingPrice,
                            0,
                          )
                          .toLocaleString("ru-RU")} ₸`
                      : "—"}
                  </Table.Cell>
                  <Table.Cell>
                    <Chip
                      color={orderStatusColor(order.status)}
                      size="sm"
                      variant="soft"
                    >
                      {orderTools.status.toString(order.status)}
                    </Chip>
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      isIconOnly
                      variant="ghost"
                      onPress={() =>
                        navigate({
                          to: "/dashboard/order/$orderId",
                          params: { orderId: String(order.id) },
                        })
                      }
                    >
                      <Icon
                        className="size-5"
                        icon="solar:arrow-right-outline"
                      />
                    </Button>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table.Content>
        </Table.Root>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center py-4">
          <Pagination>
            <Pagination.Content>
              <Pagination.Item>
                <Pagination.Previous
                  isDisabled={currentPage <= 1}
                  onPress={() => setPage(Math.max(1, currentPage - 1))}
                >
                  Назад
                </Pagination.Previous>
              </Pagination.Item>
              {paginationRange(currentPage, totalPages).map((slot, index) => (
                <Pagination.Item key={`${slot}-${index}`}>
                  {slot === PAGINATION_ELLIPSIS ? (
                    <Pagination.Ellipsis />
                  ) : (
                    <Pagination.Link
                      isActive={currentPage === slot}
                      onPress={() => setPage(slot)}
                    >
                      {slot}
                    </Pagination.Link>
                  )}
                </Pagination.Item>
              ))}
              <Pagination.Item>
                <Pagination.Next
                  isDisabled={currentPage >= totalPages}
                  onPress={() => setPage(Math.min(totalPages, currentPage + 1))}
                >
                  Вперёд
                </Pagination.Next>
              </Pagination.Item>
            </Pagination.Content>
          </Pagination>
        </div>
      )}
    </div>
  );
}
